// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerContentActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import SavePopup from './SavePopup/Izenda.SavePopup';
import CommonMessages from 'share/messages/Izenda.CommonMessages';
import { PAGE_NAME, MESSAGE_KEY, REPORT_TYPE_ENUM, ADVANCED } from 'logic/data/Izenda.ReportDesignerConstants';
import { isObservableArray } from 'mobx';
import { withState, compose, withProps, withHandlers } from 'recompose';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import localizer from 'share/localization/Izenda.Localizer';
import uiState from 'logic/Izenda.UiState';
import { curry, get, getOr, isEmpty, isNil, find } from 'lodash/fp';
import QueryStringUtil from 'share/utils/Izenda.QueryStringUtil';
import { SOURCE_PATH_QUERY_KEY } from 'logic/data/Izenda.RouteConstants';

const reportDesignerContentActions = compose(
  withState('isNew', 'updateNew', props => props.isNewDefault),
  withHandlers({
    onUpdateResults: curry(onUpdateResults),
    onAddReportPart: curry(onAddReportPart),
    onSave: curry(onSave),
    onSaveAs: curry(onSaveAs),
    onCancelReport,
    onPreview,
    handleErrorMessages
  }),
  withProps(props => {
    const { pageName } = props;
    const showUpdateResult =
      pageName === PAGE_NAME.DATA_SOURCE || pageName === PAGE_NAME.FIELDS || pageName === PAGE_NAME.FORMATS;
    return { showUpdateResult };
  })
);

function onSaved(props: any, reportId: string): void {
  const oldReportId = get('params.reportId', props);

  if (oldReportId !== reportId) {
    props.router.push({
      pathname: routeUtils.getReportDesignerUrl('ReportDesignerDetail', {
        reportId
      }),
      state: {
        keepState: true
      }
    });
  }
}

function onUpdateResults(props: any, changePageStatus: any) {
  const {
    loader,
    fieldsStore,
    fieldsStore: { reportFilters },
    store: {
      model,
      model: {
        dataSource: { reportMode }
      },
      accessStore
    },
    store
  } = props;
  const isChangePage = getOr(false, 'isChangePage', changePageStatus);
  const currentPage = get('currentPage', changePageStatus);
  const nextPage = get('nextPage', changePageStatus);

  const validateCyclePromise =
    (!isChangePage || currentPage === PAGE_NAME.DATA_SOURCE) && reportMode === ADVANCED
      ? store.validateCycleRelationships.bind(store)
      : () => Promise.resolve({ success: true });

  // we do validate if user navigate to other screen from Data Source screen
  // we also do validate if user click update resolve or click save/save as
  const needValidateRelationship = (isChangePage && currentPage === PAGE_NAME.DATA_SOURCE) || !isChangePage;

  loader.startLoading();
  store.model.setIsSavingReport(!isChangePage);
  reportFilters.resetUpdateResult();
  return Promise.join(
    validateReport(props, needValidateRelationship),
    validateCyclePromise(),
    (result, validateCycleResult) => {
      const finalResult = result.success && validateCycleResult.success;
      if (finalResult) {
        return props
          .saveDraftReport(!isChangePage, nextPage)
          .then(result =>
            props.pageName === PAGE_NAME.ACCESS
              ? accessStore
                  .initialAccessReportPage(model.reportId, model.reportAccess.draftAccesses, model.isGlobal)
                  .then(() => result)
              : Promise.resolve(result)
          );
      } else {
        const messages = result.messages || validateCycleResult.messages;
        return messages && messages.length > 0
          ? props
              .confirmFn('Information', {
                description: messages[0].messages[0],
                type: 'info',
                isHTMLFormat: true,
                showCloseIndicator: true
              })
              .then(() => finalResult)
          : Promise.resolve(finalResult);
      }
    }
  ).finally(loader.endLoading.bind(loader));
}

/**
 * Call to add new report part in report body
 */
function onAddReportPart(props: any, _: any) {
  const { store } = props;

  return store.addNewReportPart();
}

/**
 * Save the whole report
 */
function onSave(props: any, _: any) {
  const {
    store,
    loader,
    isNew,
    updateNew,
    fieldsStore,
    store: { canSaveAs },
    pageName
  } = props;
  const allowToSave = get('masterModel.allowToSave', fieldsStore);
  const changePageStatus = {
    isChangePage: false,
    currentPage: pageName,
    nextPage: pageName
  };

  store.model.setIsSavingReport(true);

  if (isNew || store.model.checkDirty() || allowToSave) {
    return onUpdateResults(props, changePageStatus)
      .then(result => {
        if (result) {
          return showFilterWarningMessage(props).then(() => (canSaveAs ? saveReport(props) : openPopup(props, false)));
        }
        return false;
      })
      .then(success => {
        success && updateNew(false);
      })
      .finally(loader.endLoading.bind(loader));
  } else {
    store.alertDetails.set({
      success: true,
      message: localizer.getText('COMMON_MESSAGES_NO_CHANGES_FOUND')
    });
  }
}

/**
 * Save As Report Designer:
 *  - Validate data > if data valid > open SaveAs Popup for new Report
 */
function onSaveAs(props: any, _: any) {
  const { store } = props;
  store.model.setIsSavingReport(true);
  store.setSaveAsMode(true);
  store.alertDetails && store.alertDetails.reset();
  const needValidateRelationship = true;
  return validateReport(props, needValidateRelationship).then(result => {
    if (result && !result.success) {
      store.model.updateErrorMessages(result.messages);
    }
    return result && result.success && showFilterWarningMessage(props).then(() => openPopup(props, true));
  });
}

/**
 * Show messages and update error indicators accordingly with
 * error messages of report designer model.
 */
function handleErrorMessages(props: any) {
  return errorMessages => {
    const {
      validation,
      pageInfo: {
        store: { model }
      }
    } = props;

    const isMessageArray = Array.isArray(errorMessages) || isObservableArray(errorMessages);
    const isNotEmptyMessagesArray = isMessageArray && !isEmpty(errorMessages);
    const isErrorMessage = !isNil(errorMessages) && !isEmpty(errorMessages);

    if (isNotEmptyMessagesArray) {
      // handle general messages
      let generalMsg = find({ key: '' }, errorMessages);
      let distinctMsg = find({ key: MESSAGE_KEY.DISTINCT }, errorMessages);
      let concurrencyMsg = find({ key: MESSAGE_KEY.CONCURRENCY }, errorMessages);

      if (!isEmpty(generalMsg) || !isEmpty(distinctMsg) || !isEmpty(concurrencyMsg)) {
        let message = generalMsg
          ? generalMsg.messages[0]
          : distinctMsg
          ? distinctMsg.messages[0]
          : concurrencyMsg.messages[0];

        props
          .confirmFn('Information', {
            description: message,
            type: CommonMessages.MESSAGE_TYPE.INFO,
            showCloseIndicator: true,
            isHTMLFormat: true
          })
          .then(() => {
            if (!isEmpty(concurrencyMsg)) {
              const {
                store,
                store: {
                  model: { reportId }
                },
                loader,
                router,
                location
              } = props;
              let query = null;
              if (store.reportListURI) {
                const spathQuery = {};
                spathQuery[`${SOURCE_PATH_QUERY_KEY}`] = store.reportListURI;
                query = QueryStringUtil.mergeQuery(location.query, spathQuery);
              }

              const viewReportDesignerUrl =
                reportId && !store.isNewReport
                  ? routeUtils.getReportDesignerUrl('ReportDesignerDetail', { reportId })
                  : routeUtils.getReportDesignerUrl();

              const confirmFn = store.model.checkDirty()
                ? props.confirmFn('Confirmation', {
                    description: store.getBundle().MESSAGE.CANCEL
                  })
                : Promise.resolve(true);

              const reportDesignerURI = { pathname: viewReportDesignerUrl };

              !store.isNewReport && query && (reportDesignerURI.query = query);

              return confirmFn
                .then(loader.startLoading.bind(loader))
                .then(store.cancelChanges.bind(store))
                .then(() => {
                  router.push(reportDesignerURI);
                })
                .finally(loader.endLoading.bind(loader));
            }
          });
      }
      // uncheck Distinct checkbox if error
      if (!isEmpty(distinctMsg)) {
        model.changeDistinct(false);
      }

      // handle message for grid
      validation.updateErrors(errorMessages);
    } else if (isErrorMessage) {
      props.confirmFn('Information', {
        description: errorMessages,
        type: CommonMessages.MESSAGE_TYPE.INFO,
        showCloseIndicator: true
      });
      props.store.model.updateErrorMessages('');
    }
  };
}

/**
 * Cancel all changes of report designer
 * @return {Promise}       An action promise
 */
function onCancelReport(props: any) {
  return () => {
    const {
      store,
      store: {
        model: { reportId }
      },
      loader,
      router,
      location
    } = props;

    let query = null;
    if (store.reportListURI) {
      const spathQuery = {};
      spathQuery[`${SOURCE_PATH_QUERY_KEY}`] = store.reportListURI;
      query = QueryStringUtil.mergeQuery(location.query, spathQuery);
    }

    const viewReportDesignerUrl =
      reportId && !store.isNewReport
        ? routeUtils.getReportDesignerUrl('ReportDesignerDetail', { reportId })
        : routeUtils.getReportDesignerUrl();

    const confirmFn = store.model.checkDirty()
      ? props.confirmFn('Confirmation', {
          description: store.getBundle().MESSAGE.CANCEL
        })
      : Promise.resolve(true);

    const reportDesignerURI = { pathname: viewReportDesignerUrl };

    !store.isNewReport && query && (reportDesignerURI.query = query);

    return confirmFn
      .then(loader.startLoading.bind(loader))
      .then(store.cancelChanges.bind(store))
      .then(() => {
        router.push(reportDesignerURI);
      })
      .finally(loader.endLoading.bind(loader));
  };
}

/**
 * Validate filter logic syntax
 * @return {Promise}            An action boolean promise
 */
function validateFilterSyntax(store: any, validation: any) {
  return store.validateFilterSyntax().then(result => {
    // Update validation errors to show the error message.
    !result.success && validation.updateErrors(result.messages);
    return Promise.resolve(result.success);
  });
}

/**
 * Open SavePopup
 * @return {Promise}        A popup promise
 */
function openPopup(props: any, saveAs: boolean) {
  const store = props.store;
  // Set Report type when open save popup
  // Fieldless report : Template is default.

  if (store.disableReportType) {
    store.model.setReportType(REPORT_TYPE_ENUM.TEMPLATE);
  } else {
    store.model.setReportType(REPORT_TYPE_ENUM.REPORT);
  }

  const loadPromise =
    isNil(store.model.categoryId) || store.saveAsMode
      ? store.loadCategories()
      : Promise.join(store.loadCategories(), store.loadSubCategories(store.model.categoryId));

  return loadPromise.then(() =>
    props.openPopup(SavePopup, {
      store,
      saveAsMode: saveAs,
      isNew: props.isNew,
      section: props.pageName,
      onSaved: curry(onSaved)(props),
      disableReport: props.disableReport
    })
  );
}

function showFilterWarningMessage(props: any) {
  const { store, pageName } = props;
  const warningMessage = store.getUnspecifiedFilterWarning();
  if (warningMessage && (pageName === PAGE_NAME.FIELDS || pageName === PAGE_NAME.FORMATS)) {
    return props.confirmFn('Information', {
      type: CommonMessages.MESSAGE_TYPE.INFO_ACCEPT,
      description: warningMessage,
      showCancel: false
    });
  } else {
    return Promise.resolve(false);
  }
}

/**
 * Validate all rules for current report
 * Validation steps:
 *   + Validate Filter syntax
 *   + Detect report changes
 *   + Validate Relationship Syntax
 */
function validateReport(props: any, needValidateRelationship: boolean) {
  const {
    validation,
    store,
    store: {
      dataSourceStore: { dataTree },
      model: {
        dataSource: { reportMode }
      }
    },
    pageName,
    localization
  } = props;

  const validateRelationshipPromise = needValidateRelationship
    ? reportMode === ADVANCED
      ? store.validateRelationshipSyntax.bind(store)
      : store.validateSimpleModeRelationship.bind(store)
    : () => Promise.resolve({ success: true });

  store.resetAlertDetails();

  const handleValidationSteps = () =>
    validation
      .validate()
      .then(success => {
        !success &&
          store.setAlertDetails({
            errors: [{ message: localizer.getText('COMMON_MESSAGES_GENERAL_ERROR') }],
            success: false
          });
        return success ? props.detectReportChange(props, pageName) : { success: false };
      })
      .then(result => {
        return result.success ? validateRelationshipPromise() : result;
      })
      .then(result => {
        if (result && result.success) {
          return store.validateReport();
        }
        return result;
      })
      .then(result => {
        if (result && !result.success && !isEmpty(result.messages)) {
          const messageHasKey = find(function(m) {
            return !isEmpty(m.key);
          }, result.messages);
          if (!isNil(messageHasKey)) {
            validation.updateErrors(result.messages);
            return { success: false };
          }
          return result;
        }
        return { success: result.success };
      })
      .then(result => {
        if (result && result.success) {
          return store.detectSchemaChange(pageName);
        }
        return result;
      })
      .then(result => {
        result = result || { success: false };

        if (result.success === false) {
          return result;
        }

        // Change Schema
        if (result.hasChanged) {
          if(!dataTree.hasDynamicStore()){
            props.confirmMessage('error', localization.getText('REPORTDESIGNER_MESSAGE_CHANGE_SCHEMA_NON_DYNAMIC'));
            return { success: false };
          }

          // In Data Source page
          if (pageName === PAGE_NAME.DATA_SOURCE) {
            return store.updateResults();
          } else {
            // In Field Page
            props.confirmMessage('info', localization.getText('REPORTDESIGNER_MESSAGE_CHANGE_SCHEMA_DYNAMIC'));
            return { success: false };
          }
        }

        return { success: true };
      }).then(result => {
        return result || { success: false };
      });

  return validateFilterSyntax(store, validation).then(valid => (valid ? handleValidationSteps() : { success: false }));
}

/**
 * Save report
 */
function saveReport(props: any) {
  const { store, pageName, disableReport } = props;
  store.model.setIgnoreCheckChange();

  return store.saveReport(pageName).then(result => {
    result.isDisableReport && disableReport();
    return result.success;
  });
}

/**
 * On click Preview button
 * @return {Promise} An action promise
 */
function onPreview(props: any) {
  return () => {
    const {
      store,
      store: {
        model: { reportId },
        canSaveAs
      },
      localization,
      router
    } = props;

    let query = null;
    if (store.reportListURI) {
      const spathQuery = {};
      spathQuery[`${SOURCE_PATH_QUERY_KEY}`] = store.reportListURI;
      query = QueryStringUtil.mergeQuery(props.location.query, spathQuery);
    }

    var preview = () => {
      var viewReportUrl = {
        pathname: routeUtils.getReportUrl('View', { id: reportId }),
        state: {
          fromReportDesigner: true
        }
      };

      query && (viewReportUrl.query = query);

      router.push(viewReportUrl);
    };

    if (!canSaveAs) {
      return props.confirmMessage('warning', localization.getText('REPORTDESIGNER_MESSAGE_SAVE_BEFORE_PREVIEW'));
    } else {
      return props
        .confirmDirtyForm(uiState.isDirty, {
          onConfirmDirtyForm: uiState.onOkDirtyForm,
          customMessage: localization.getText('REPORTDESIGNER_MESSAGE_CONFIRM_PREVIEW')
        })
        .then(preview);
    }
  };
}

export default reportDesignerContentActions;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Izenda.ReportDesignerContentActions.js